/**
 * 導覽層的薄封裝。
 *
 * 頁面元件只從這裡拿 Link / useParams / useRouter / useSearchParams，
 * 不直接 import react-router —— 導覽這件事集中在一個模組，
 * 之後要換路由庫或改導覽行為時只有這裡要動。
 *
 * useParams 回傳的是「已解碼」的值 —— 跟 react-router 一致。
 * 頁面拿到 code 直接用，不要再 decodeURIComponent 一次。
 */

import { useMemo } from "react";
import {
  Link as RouterLink,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from "react-router";

type LinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & { href: string };

export function Link({ href, children, ...rest }: LinkProps) {
  return (
    <RouterLink to={href} {...rest}>
      {children}
    </RouterLink>
  );
}

export function useParams<T extends Record<string, string | undefined>>(): T {
  return useRouterParams() as T;
}

export function useSearchParams(): URLSearchParams {
  const [params] = useRouterSearchParams();
  return params;
}

export interface Router {
  push(href: string): void;
  replace(href: string): void;
  back(): void;
}

export function useRouter(): Router {
  const navigate = useNavigate();
  return useMemo(
    () => ({
      push: (href: string) => void navigate(href),
      replace: (href: string) => void navigate(href, { replace: true }),
      back: () => void navigate(-1),
    }),
    [navigate],
  );
}
